<template>
  <div class="discount-rule-preview responsive-menu">
    <div class="responsive-menu__child">
      <PageHeader
        :pageHeader="previewPageHeader"
        :links="[
          { name: 'home', label: 'Home' },
          { name: 'discountRules', label: 'Discount Rules' },
          { name: 'discountRulePreview', label: 'Preview' },
        ]"
        :entity="entity"
        :canEdit="false"
        :hideAddButton="true"
      />

      <div class="section-base">
        <!-- Input Section -->
        <div class="padded elevated-0 bordered">
          <h3 class="discount-rule-preview__section-title">Test Discount Rules</h3>
          <p class="discount-rule-preview__description">
            Enter an order code to preview how discount rules would apply to that order.
          </p>

          <div class="discount-rule-preview__input-row">
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Order Code</label>
              <input
                v-model="orderCode"
                class="discount-rule-preview__input"
                placeholder="Enter order code..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Customer ID (Optional)</label>
              <input
                v-model="customerId"
                class="discount-rule-preview__input"
                placeholder="Enter customer ID..."
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Coupon Codes (Optional)</label>
              <input
                v-model="couponCodes"
                class="discount-rule-preview__input"
                placeholder="Enter coupon codes..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Employee PIN (Optional)</label>
              <input
                v-model="employeePIN"
                class="discount-rule-preview__input"
                type="password"
                placeholder="Enter employee PIN..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Instant Percent Off (Optional)</label>
              <input
                v-model="instantPercentOff"
                type="number"
                class="discount-rule-preview__input"
                placeholder="Enter percentage..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Instant Dollar Off (Optional)</label>
              <input
                v-model="instantDollarOff"
                type="number"
                step="0.01"
                class="discount-rule-preview__input"
                placeholder="Enter dollar amount..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field">
              <label class="discount-rule-preview__label">Reason (Optional)</label>
              <input
                v-model="reason"
                class="discount-rule-preview__input"
                placeholder="Enter reason..."
                @keyup.enter="runPreview"
              />
            </div>
            <div class="discount-rule-preview__field discount-rule-preview__field--button">
              <button
                class="discount-rule-preview__run-btn"
                :disabled="!orderCode || isLoading"
                @click="runPreview"
              >
                <i v-if="isLoading" class="fa fa-spinner fa-spin"></i>
                <i v-else class="fa fa-play"></i>
                {{ isLoading ? 'Running...' : 'Run Preview' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Results Section -->
        <div v-if="previewResult && previewResult.discountResult" class="padded elevated-0 bordered discount-rule-preview__results">
          <h3 class="discount-rule-preview__section-title">Preview Results</h3>

          <!-- Order Info -->
          <div class="discount-rule-preview__order-info">
            <div class="discount-rule-preview__stat">
              <span class="discount-rule-preview__stat-label">Order Code</span>
              <span class="discount-rule-preview__stat-value">{{ previewResult.order.code }}</span>
            </div>
            <div class="discount-rule-preview__stat">
              <span class="discount-rule-preview__stat-label">Order Type</span>
              <span class="discount-rule-preview__stat-value discount-rule-preview__stat-value--type">{{ previewResult.order.type || 'N/A' }}</span>
            </div>
            <div class="discount-rule-preview__stat">
              <span class="discount-rule-preview__stat-label">Subtotal</span>
              <span class="discount-rule-preview__stat-value">${{ formatMoney(previewResult.discountResult.totals.originalSubTotal) }}</span>
            </div>
            <div class="discount-rule-preview__stat discount-rule-preview__stat--tax">
              <span class="discount-rule-preview__stat-label">Tax</span>
              <span class="discount-rule-preview__stat-value">${{ formatMoney(previewResult.discountResult.totals.tax) }}</span>
            </div>
            <div class="discount-rule-preview__stat discount-rule-preview__stat--highlight">
              <span class="discount-rule-preview__stat-label">Total Discount</span>
              <span class="discount-rule-preview__stat-value">-${{ formatMoney(previewResult.discountResult.totals.totalDiscount) }}</span>
            </div>
            <div class="discount-rule-preview__stat discount-rule-preview__stat--final">
              <span class="discount-rule-preview__stat-label">Order Total</span>
              <span class="discount-rule-preview__stat-value">${{ formatMoney(previewResult.discountResult.totals.orderTotal) }}</span>
            </div>
          </div>

          <!-- Toggle Buttons Row -->
          <div class="discount-rule-preview__toggle-buttons">
            <!-- Debug Toggle Button -->
            <button
              v-if="previewResult._debug"
              class="discount-rule-preview__debug-btn"
              @click="showTaxDebug = !showTaxDebug"
            >
              <i :class="['fa', showTaxDebug ? 'fa-eye-slash' : 'fa-bug']"></i>
              {{ showTaxDebug ? 'Hide' : 'Show' }} Tax Debug
            </button>

            <!-- POS View Toggle Button -->
            <button
              class="discount-rule-preview__pos-view-btn"
              @click="showPosView = !showPosView"
            >
              <i :class="['fa', showPosView ? 'fa-columns' : 'fa-shopping-cart']"></i>
              {{ showPosView ? 'Hide' : 'Show' }} POS View
            </button>
          </div>

          <!-- Debug Info (toggleable) -->
          <div v-if="previewResult._debug && showTaxDebug" class="discount-rule-preview__debug">
            <h4 class="discount-rule-preview__subsection-title">
              <i class="fa fa-bug"></i> Tax Debug Info
            </h4>
            <div class="discount-rule-preview__debug-content">
              <div><strong>Tax Setups Found:</strong> {{ previewResult._debug.taxSetupCount }}</div>
              <div><strong>Cannabis All-In Tax:</strong> {{ previewResult._debug.isCannabisAllInTaxEnabled }}</div>
              <div><strong>Non-Cannabis All-In Tax:</strong> {{ previewResult._debug.isNonCannabisAllInTaxEnabled }}</div>
              <div><strong>Has All-In Tax:</strong> {{ previewResult._debug.hasAllInTax }}</div>
              <div><strong>First Item Price:</strong> ${{ previewResult._debug.firstItemPrice }}</div>
              <div><strong>Tax Returned (add-on):</strong> ${{ previewResult._debug.taxTotalReturned }}</div>
              <div><strong>Embedded Tax Total:</strong> ${{ previewResult._debug.embeddedTaxTotal }}</div>
              <div><strong>Taxes Applied Count:</strong> {{ previewResult._debug.taxesAppliedCount }}</div>
              <div v-if="previewResult._debug.taxesAppliedDetails && previewResult._debug.taxesAppliedDetails.length > 0">
                <strong>Taxes Applied:</strong>
                <ul>
                  <li v-for="(t, idx) in previewResult._debug.taxesAppliedDetails" :key="'tax-'+idx">
                    {{ t.taxName }}: ${{ t.taxAmount }} ({{ (t.taxRate * 100).toFixed(2) }}%) - allIn: {{ t.allIn }}
                  </li>
                </ul>
              </div>
              <div v-if="previewResult.order.products && previewResult.order.products.length > 0">
                <strong>Products:</strong>
                <ul>
                  <li v-for="(p, idx) in previewResult.order.products" :key="idx">
                    {{ p.name }} - isCannabis: {{ p.isCannabis }}, isTaxable: {{ p.isTaxable }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Split Layout Container -->
          <div class="discount-rule-preview__content discount-rule-preview__content--split">
            <!-- Results Panel (left side) -->
            <div class="discount-rule-preview__results-panel">
              <!-- Applied Rules -->
              <div v-if="filteredAppliedRules.length > 0" class="discount-rule-preview__applied">
                <h4 class="discount-rule-preview__subsection-title">
                  <i class="fa fa-check-circle"></i> Applied Rules ({{ filteredAppliedRules.length }})
                </h4>
                <div class="discount-rule-preview__rules-list">
                  <div
                    v-for="(rule, index) in filteredAppliedRules"
                    :key="'applied-' + index"
                    class="discount-rule-preview__rule-card discount-rule-preview__rule-card--applied"
                  >
                    <div class="discount-rule-preview__rule-header">
                      <span class="discount-rule-preview__rule-name">{{ rule.ruleName || rule.ruleId }}</span>
                      <span class="discount-rule-preview__rule-discount">-${{ formatMoney(rule.totalDiscount) }}</span>
                    </div>
                    <div class="discount-rule-preview__rule-details">
                      <span class="discount-rule-preview__rule-type">{{ discountTypeLabelMap[rule.discountType] || rule.discountType }}</span>
                      <span v-if="rule.itemsDiscounted" class="discount-rule-preview__rule-items">
                        {{ rule.itemsDiscounted }} item(s) affected
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Evaluated Rules (not applied) -->
              <div v-if="previewResult.discountResult && previewResult.discountResult.skippedRules && previewResult.discountResult.skippedRules.length > 0" class="discount-rule-preview__evaluated">
                <h4 class="discount-rule-preview__subsection-title">
                  <i class="fa fa-times-circle"></i> Evaluated but Not Applied ({{ previewResult.discountResult.skippedRules.length }})
                </h4>
                <div class="discount-rule-preview__rules-list">
                  <div
                    v-for="(rule, index) in previewResult.discountResult.skippedRules"
                    :key="'evaluated-' + index"
                    class="discount-rule-preview__rule-card discount-rule-preview__rule-card--not-applied"
                  >
                    <div class="discount-rule-preview__rule-header">
                      <span class="discount-rule-preview__rule-name">{{ rule.ruleName || rule.ruleId }}</span>
                    </div>
                    <div class="discount-rule-preview__rule-reason">
                      {{ rule.reason || 'Conditions not met' }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Items Meta Preview -->
          <div v-if="previewResult.discountResult && previewResult.discountResult.itemsMeta && previewResult.discountResult.itemsMeta.length > 0" class="discount-rule-preview__items">
            <h4 class="discount-rule-preview__subsection-title">
              <i class="fa fa-list"></i> Items Breakdown
            </h4>
            <div class="discount-rule-preview__items-table">
              <div class="discount-rule-preview__items-header">
                <span></span>
                <span>Product</span>
                <span>Qty</span>
                <span>Unit Price</span>
                <span>Discount</span>
                <span>Final</span>
              </div>
              <template v-for="(item, index) in previewResult.discountResult.itemsMeta">
                <div
                  :key="'item-row-' + index"
                  class="discount-rule-preview__item-row"
                  :class="{ 'discount-rule-preview__item-row--expandable': item.discountsApplied && item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM').length > 0 }"
                  @click="toggleItemExpand(index)"
                >
                  <span class="discount-rule-preview__item-toggle">
                    <i
                      v-if="item.discountsApplied && item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM').length > 0"
                      :class="['fa', expandedItems[index] ? 'fa-chevron-down' : 'fa-chevron-right']"
                    ></i>
                  </span>
                  <span class="discount-rule-preview__item-name">
                    {{ item.productName || item.productId }}
                    <span v-if="item.supplierName || item.brandName || item.groupName" class="discount-rule-preview__item-meta">
                      {{ [item.supplierName, item.brandName, item.groupName].filter(Boolean).join(' · ') }}
                    </span>
                  </span>
                  <span>{{ item.quantity }}</span>
                  <span>${{ formatMoney(item.originalUnitPrice) }}</span>
                  <span class="discount-rule-preview__item-discount">
                    {{ item.totalDiscount > 0 ? '-$' + formatMoney(item.totalDiscount) : '-' }}
                  </span>
                  <span>${{ formatMoney(item.discountedSubTotal) }}</span>
                </div>
                <!-- Expanded Discounts Sub-rows -->
                <div
                  :key="'item-discounts-' + index"
                  v-if="expandedItems[index] && item.discountsApplied && item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM').length > 0"
                  class="discount-rule-preview__item-discounts"
                >
                  <div
                    v-for="(discount, dIndex) in item.discountsApplied.filter(d => d.effectType !== 'QUALIFYING_ITEM')"
                    :key="'discount-' + index + '-' + dIndex"
                    class="discount-rule-preview__discount-row"
                  >
                    <span class="discount-rule-preview__discount-indicator"></span>
                    <span class="discount-rule-preview__discount-name">
                      <i class="fa fa-tag"></i>
                      {{ discount.ruleName }}
                    </span>
                    <span class="discount-rule-preview__discount-type">{{ discountTypeLabelMap[discount.discountType] || discount.discountType }}</span>
                    <span class="discount-rule-preview__discount-effect">
                      {{ formatEffectType(discount.effectType, discount.effectValue) }}
                    </span>
                    <span class="discount-rule-preview__discount-amount">
                      -${{ formatMoney(discount.totalDiscount) }}
                    </span>
                    <span></span>
                  </div>
                </div>
              </template>
            </div>
          </div>
            </div>

            <!-- POS Cart Panel (right side, slides in/out) -->
            <div :class="['discount-rule-preview__pos-panel', { 'discount-rule-preview__pos-panel--open': showPosView }]">
              <h4 class="discount-rule-preview__subsection-title">
                <i class="fa fa-shopping-cart"></i> POS Cart View
              </h4>
              <PosCartItemized v-if="posPreviewOrder" :order="posPreviewOrder" />
              <div v-else class="discount-rule-preview__pos-empty">
                No items to display
              </div>

              <!-- Order Summary -->
              <div v-if="posPreviewOrder" class="discount-rule-preview__order-summary-wrapper">
                <PosOrderDetails :order="posPreviewOrder" :store="currentStore" />
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!isLoading" class="padded elevated-0 bordered discount-rule-preview__empty">
          <i class="fa fa-flask discount-rule-preview__empty-icon"></i>
          <p>Enter an order code above to preview discount calculations</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import PosCartItemized from "@/views/Pos/components/PosCart/PosCartItemized.vue";
import PosOrderDetails from "@/views/Pos/components/PosOrderDetails/PosOrderDetails.vue";
import { mapActions, mapGetters } from "vuex";
import Entities from "@/resources/Entities";
import { discountTypeLabelMap, effectTypeLabelMap  } from "@/constants/discountRule";

export default {
  name: "discountRulePreview",
  data() {
    return {
      orderCode: "1289429",
      customerId: "",
      couponCodes: [],
      employeeId: "",
      instantPercentOff: "",
      instantDollarOff: "",
      reason: "",
      entity: Entities.DISCOUNTRULE,
      expandedItems: {},
      showTaxDebug: false,
      showPosView: false,
      previewPageHeader: {
        title: "Discount Rule Preview",
        isEditorView: false,
        cancelPath: "discountRules",
      },
      discountTypeLabelMap,
      effectTypeLabelMap,
    };
  },
  components: {
    PageHeader,
    PosCartItemized,
    PosOrderDetails,
  },
  mounted() {
    this.setPageTitle("Discount Rule Preview");
  },
  computed: {
    ...mapGetters({
      previewResult: "discountRuleModule/previewResult",
      loadingStatus: "discountRuleModule/loadingStatus",
      currentStore: "storeModule/currentStore"
    }),
    isLoading() {
      return this.loadingStatus.previewOrder;
    },
    posPreviewOrder() {
      if (!this.previewResult?.discountResult) return null;
      const totals = this.previewResult.discountResult.totals || {};
      return {
        ...this.previewResult.order,
        itemsMeta: this.previewResult.discountResult.itemsMeta,
        // Convert cents to dollars for PosOrderDetails
        subTotal: (totals.originalSubTotal || 0) / 100,
        discount: (totals.totalDiscount || 0) / 100,
        // Use tax directly (add-on tax, 0 for all-in items)
        tax: (totals.tax || 0) / 100,
        orderTotal: (totals.orderTotal || 0) / 100,
        hasAllInTax: totals.hasAllInTax || false,
      };
    },
    filteredAppliedRules() {
      if (!this.previewResult?.discountResult?.appliedRules) return [];
      return this.previewResult.discountResult.appliedRules.filter(
        r => r.discountType !== 'rounding'
      );
    },
  },
  methods: {
    ...mapActions({
      previewOrder: "discountRuleModule/previewOrder",
      clearPreviewResult: "discountRuleModule/clearPreviewResult",
    }),

    runPreview() {
      if (!this.orderCode) return;

      const payload = {
        orderCode: this.orderCode,
        customerId: this.customerId || null,
        couponCodes: this.couponCodes || [],
      };

      // Add optional fields only if they have values
      if (this.employeePIN) payload.employeePIN = this.employeePIN;
      if (this.instantPercentOff) payload.instantPercentOff = parseFloat(this.instantPercentOff);
      if (this.instantDollarOff) payload.instantDollarOff = parseFloat(this.instantDollarOff);
      if (this.reason) payload.reason = this.reason;

      this.previewOrder(payload);
    },

    formatMoney(value) {
      if (value === null || value === undefined) return "0.00";
      return parseFloat(value/100).toFixed(2);
    },

    getTotals(field) {
      if (this.previewResult && this.previewResult.totals) {
        return this.previewResult.totals[field];
      }
      return null;
    },

    toggleItemExpand(index) {
      this.$set(this.expandedItems, index, !this.expandedItems[index]);
    },

    formatEffectType(effectType, effectValue) {
      switch (effectType) {
        case 'PERCENT_OFF':
          return `${effectValue}% off`;
        case 'FIXED_AMOUNT_OFF':
          return `$${effectValue} off`;
        case 'FIXED_PRICE':
          return `Fixed $${effectValue}`;
        case 'BOGO':
          return 'BOGO';
        case 'FREE':
          return 'Free';
        case 'DOLLAR_OFF':
          return `$${effectValue} off`;
        default:
          return effectTypeLabelMap[effectType] || effectType;
      }
    },
  },
  beforeDestroy() {
    this.clearPreviewResult();
  },
};
</script>

<style lang="scss" scoped>
.discount-rule-preview {
  &__section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  &__description {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 1.5rem;
  }

  &__input-row {
    display: flex;
    gap: 1rem;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  &__field {
    flex: 1;
    min-width: 200px;

    &--button {
      flex: 0 0 auto;
      min-width: auto;
    }
  }

  &__label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
  }

  &__input {
    width: 100%;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: inherit;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #60a5fa;
    }
  }

  &__run-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 10px 20px;
    background: #10b981;
    border: none;
    border-radius: 6px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: #059669;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__results {
    margin-top: 1.5rem;
  }

  &__order-info {
    display: flex;
    gap: 2rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  &__stat {
    text-align: center;

    &--highlight {
      .discount-rule-preview__stat-value {
        color: #10b981;
      }
    }

    &--tax {
      .discount-rule-preview__stat-value {
        color: #f59e0b;
      }
    }

    &--final {
      .discount-rule-preview__stat-value {
        color: #60a5fa;
        font-size: 20px;
      }
    }
  }

  &__stat-value--type {
    text-transform: capitalize;
    background: rgba(96, 165, 250, 0.15);
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 14px;
  }

  &__stat-label {
    display: block;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }

  &__stat-value {
    font-size: 18px;
    font-weight: 600;
  }

  &__subsection-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.9);

    i {
      font-size: 16px;
    }
  }

  &__applied {
    margin-bottom: 1.5rem;

    .discount-rule-preview__subsection-title {
      color: #10b981;
    }
  }

  &__evaluated {
    margin-bottom: 1.5rem;

    .discount-rule-preview__subsection-title {
      color: #ef4444;
    }
  }

  &__rules-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__rule-card {
    padding: 1rem;
    border-radius: 6px;

    &--applied {
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.2);
    }

    &--not-applied {
      background: rgba(239, 68, 68, 0.05);
      border: 1px solid rgba(239, 68, 68, 0.15);
    }
  }

  &__rule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  &__rule-name {
    font-weight: 600;
    font-size: 14px;
  }

  &__rule-discount {
    font-weight: 600;
    color: #10b981;
  }

  &__rule-details {
    display: flex;
    gap: 1rem;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
  }

  &__rule-type {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
  }

  &__rule-reason {
    font-size: 12px;
    color: rgba(239, 68, 68, 0.8);
  }

  &__items-table {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    overflow: hidden;
  }

  &__items-header {
    display: grid;
    grid-template-columns: 30px 2fr 0.5fr 1fr 1fr 1fr;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: rgba(0, 0, 0, 0.2);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
  }

  &__item-row {
    display: grid;
    grid-template-columns: 30px 2fr 0.5fr 1fr 1fr 1fr;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 13px;

    &--expandable {
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.03);
      }
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__item-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;

    i {
      transition: transform 0.2s ease;
    }
  }

  &__item-name {
    font-weight: 500;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item-meta {
    font-size: 11px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
  }

  &__item-discount {
    color: #10b981;
  }

  &__item-discounts {
    background: rgba(16, 185, 129, 0.05);
    border-left: 3px solid rgba(16, 185, 129, 0.3);
    margin-left: 30px;
  }

  &__discount-row {
    display: grid;
    grid-template-columns: 20px 2fr 1fr 1fr 1fr 0.5fr;
    gap: 1rem;
    padding: 0.5rem 1rem;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);

    &:last-child {
      border-bottom: none;
    }
  }

  &__discount-indicator {
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: rgba(16, 185, 129, 0.5);
    }
  }

  &__discount-name {
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);

    i {
      margin-right: 0.5rem;
      color: #10b981;
      font-size: 10px;
    }
  }

  &__discount-type {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10px;
    text-transform: uppercase;
    width: fit-content;
  }

  &__discount-effect {
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }

  &__discount-amount {
    color: #10b981;
    font-weight: 600;
  }

  &__empty {
    text-align: center;
    padding: 4rem 2rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 1.5rem;
  }

  &__empty-icon {
    font-size: 48px;
    margin-bottom: 1rem;
    opacity: 0.3;
  }

  &__debug-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(239, 68, 68, 0.1);
    border: 1px dashed rgba(239, 68, 68, 0.3);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
      background: rgba(239, 68, 68, 0.2);
      border-color: rgba(239, 68, 68, 0.5);
    }

    i {
      font-size: 12px;
    }
  }

  &__debug {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(239, 68, 68, 0.1);
    border: 1px dashed rgba(239, 68, 68, 0.3);
    border-radius: 6px;

    .discount-rule-preview__subsection-title {
      color: #ef4444;
    }
  }

  &__debug-content {
    font-size: 11px;
    font-family: monospace;
    color: rgba(255, 255, 255, 0.8);

    div {
      margin-bottom: 0.5rem;
    }

    pre {
      background: rgba(0, 0, 0, 0.3);
      padding: 0.75rem;
      border-radius: 4px;
      overflow-x: auto;
      max-height: 300px;
      margin: 0.5rem 0 1rem 0;
      font-size: 10px;
      line-height: 1.4;
    }
  }

  &__toggle-buttons {
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  &__pos-view-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(96, 165, 250, 0.1);
    border: 1px dashed rgba(96, 165, 250, 0.3);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
      background: rgba(96, 165, 250, 0.2);
      border-color: rgba(96, 165, 250, 0.5);
    }

    i {
      font-size: 12px;
    }
  }

  &__content {
    display: flex;
    gap: 1.5rem;
  }

  &__pos-panel {
    flex: 0 0 0%;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    padding: 0;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    position: relative;
    transition: flex-basis 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.25s ease 0.1s,
                padding 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    &--open {
      flex: 0 0 33.333%;
      max-width: 33.333%;
      opacity: 1;
      padding: 1rem;
      overflow: auto;
    }

    .discount-rule-preview__subsection-title {
      color: #60a5fa;
    }
  }

  &__pos-empty {
    text-align: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.4);
    font-style: italic;
  }

  &__order-summary-wrapper {
    margin-top: 1rem;

    // Hide control buttons in preview mode
    :deep(.pos-order-details__control-buttons) {
      display: none;
    }
  }

  &__results-panel {
    flex: 1;
    min-width: 0;
    overflow: auto;
    transition: flex 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>

